/**
 * Which date a portfolio agreement request is about depends on its type.
 *
 * An appendix carries its own date in `agreementDate`, so the portfolio's open
 * date is noise there. An amendment / portfolio agreement is dated by the
 * portfolio open date — that *is* its agreement date — so it has no separate
 * value of its own.
 *
 * Every surface reads the date through here so the table, the review details
 * and the approval picker cannot disagree about which one applies.
 */
export const APPENDIX_TYPE = "APPENDIX";

export const isAppendix = (type?: string | null): boolean =>
  (type ?? "").trim().toUpperCase() === APPENDIX_TYPE;

/**
 * Label for the record's applicable date, and the single source for it — the
 * review field, the approval dialog and the validation message all read this,
 * so an error can never name a field the WM cannot see. An agreement is dated
 * by the portfolio open date, which is what that field is called on screen.
 */
export const dateLabel = (type?: string | null): string =>
  isAppendix(type) ? "Appendix date" : "Portfolio open date";

/** Appendices are dated by their own date, not the portfolio's open date. */
export const showsPortfolioOpenDate = (type?: string | null): boolean =>
  !isAppendix(type);

/** Only an appendix surfaces `agreementDate` as its own detail row. */
export const showsAgreementDate = (type?: string | null): boolean =>
  isAppendix(type);

/**
 * The date that applies to a record: an appendix's own `agreementDate`, or the
 * portfolio open date for everything else.
 */
export const applicableDate = (record: any): string | null =>
  (isAppendix(record?.type) ? record?.agreementDate : record?.portfolioOpenDate) ??
  null;

/**
 * Query/body key the applicable date is sent under.
 *
 * The preview endpoint accepts both `portfolioOpenDate` and `agreementDate`;
 * which one a record uses follows the same rule as everything else here — an
 * appendix is dated by `agreementDate`, an agreement by `portfolioOpenDate`.
 */
export const dateParamKey = (
  type?: string | null
): "agreementDate" | "portfolioOpenDate" =>
  isAppendix(type) ? "agreementDate" : "portfolioOpenDate";

/** `{ agreementDate: iso }` or `{ portfolioOpenDate: iso }`, or `{}` if unset. */
export const dateParams = (
  type?: string | null,
  iso?: string | null
): Record<string, string> => (iso ? { [dateParamKey(type)]: iso } : {});

/**
 * What approval requires, by type.
 *
 * An appendix needs its own date **and** the new fees; an amendment /
 * portfolio agreement needs only its date. Kept beside the date rules so the
 * two cannot drift — the review screen reads both from here.
 */
export const requiresNewFees = (type?: string | null): boolean =>
  isAppendix(type);

/**
 * Request key for the fee — the same name the record carries it under, so the
 * value round-trips unchanged. Named here rather than inlined so there is one
 * place to change it if the endpoint ever diverges from the record.
 */
export const FEES_PARAM_KEY = "newFees";

/** `{ newFees: "0.125" }`, or `{}` when the type does not need it or it is unset. */
export const feesParams = (
  type?: string | null,
  value?: string | null
): Record<string, string> => {
  const trimmed = (value ?? "").trim();
  if (!requiresNewFees(type) || !trimmed) return {};
  return { [FEES_PARAM_KEY]: trimmed };
};

/** A fee is a decimal; values like `.125` are normal in this data. */
export const isValidFee = (value?: string | null): boolean => {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return false;
  return /^\d*\.?\d+$/.test(trimmed) && Number.isFinite(Number(trimmed));
};

export interface ApprovalDraft {
  type?: string | null;
  date: string | null;
  newFees: string | null;
}

/**
 * Reasons approval is blocked, in the order they should be shown. Empty means
 * the request can be approved.
 */
export const approvalErrors = ({
  type,
  date,
  newFees,
}: ApprovalDraft): string[] => {
  const errors: string[] = [];
  if (!date) errors.push(`${dateLabel(type)} is required.`);
  if (requiresNewFees(type)) {
    // The fee is set at initiation and only displayed on this screen, so the
    // message says where it comes from — "is required" would read as something
    // the reviewer forgot to enter.
    if (!(newFees ?? "").trim()) {
      errors.push(
        "New fees is missing on this request. It is set at initiation and cannot be edited here."
      );
    } else if (!isValidFee(newFees)) {
      errors.push("New fees on this request is not a valid number.");
    }
  }
  return errors;
};
